import React, { useState, useEffect } from 'react';
import { PageHeader } from '../design';
import { useParams, useNavigate } from 'react-router-dom';
import { useSuperAdminAuth } from '../../../contexts/SuperAdminAuthContext';
import { governanceAPI } from '../../../lib/governanceApi';
import { toast } from 'sonner';
import {
  Building2,
  ArrowLeft,
  RefreshCw,
  Users,
  Shield,
  Activity,
  DollarSign,
  TrendingUp,
  Mail,
  Calendar,
  FileCheck,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Power,
  PowerOff,
  Zap,
  UserCheck,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Skeleton } from '../../../components/ui/skeleton';
import { Progress } from '../../../components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import TenantOpsActions from '../components/TenantOpsActions';

export default function TenantDetail() {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useSuperAdminAuth();
  const [loading, setLoading] = useState(true);
  const [tenantData, setTenantData] = useState(null);
  const [billingHistory, setBillingHistory] = useState(null);

  useEffect(() => {
    if (isAuthenticated && tenantId) {
      fetchTenantDetail();
      governanceAPI.getTenantBillingHistory(tenantId)
        .then((r) => setBillingHistory(r.data))
        .catch(() => setBillingHistory({ events: [], ever_paid_plan: null }));
    }
  }, [isAuthenticated, tenantId]);

  const fetchTenantDetail = async () => {
    setLoading(true);
    try {
      const res = await governanceAPI.getTenantDetail(tenantId);
      setTenantData(res.data);
    } catch (error) {
      console.error('Failed to fetch tenant detail:', error);
      toast.error('Failed to load tenant details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return `$${(amount || 0).toFixed(2)}`;
  };

  const getMaxExecutions = () => {
    if (!tenantData?.activity_trend) return 1;
    return Math.max(...tenantData.activity_trend.map(d => d.executions), 1);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10" />
          <Skeleton className="h-8 w-64" />
        </div>
        <div className="grid gap-4 md:grid-cols-4">
          <Skeleton className="h-28" />
          <Skeleton className="h-28" />
          <Skeleton className="h-28" />
          <Skeleton className="h-28" />
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (!tenantData) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Shield className="h-12 w-12 text-muted-foreground mb-4" />
        <h2 className="text-xl font-semibold mb-2">Tenant Not Found</h2>
        <p className="text-muted-foreground mb-4">The requested tenant could not be loaded.</p>
        <Button onClick={() => navigate('/governance/tenants')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Tenants
        </Button>
      </div>
    );
  }

  const tenant = tenantData.tenant;

  return (
    <div className="space-y-6" data-testid="tenant-detail">
      {/* Header */}
      <PageHeader
        breadcrumb={
          <button onClick={() => navigate('/governance/tenants')} className="inline-flex items-center gap-1.5 hover:gov-text-primary transition-colors">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Tenants
          </button>
        }
        title={tenant.name || 'Unknown Tenant'}
        subtitle={
          <span className="inline-flex items-center gap-2">
            <span className="font-mono">{tenantId}</span>
            <span>•</span>
            <Building2 className="h-3 w-3" />
            <span>{tenantData.organization?.name || 'Unknown Org'}</span>
          </span>
        }
        actions={
          <>
            {tenant.kill_switch_active ? (
              <Badge variant="destructive" className="text-sm px-3 py-1">
                <PowerOff className="h-4 w-4 mr-1" />
                AI PAUSED
              </Badge>
            ) : (
              <Badge variant="default" className="bg-green-600 text-sm px-3 py-1">
                <Power className="h-4 w-4 mr-1" />
                AI ENABLED
              </Badge>
            )}
            {tenant.is_demo && (
              <Badge variant="secondary">DEMO</Badge>
            )}
            <Button variant="outline" size="icon" onClick={fetchTenantDetail}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </>
        }
      />

      {/* Phase 7: Governor ops actions — retry onboarding, grant credits, impersonate */}
      <TenantOpsActions
        tenantId={tenantId}
        tenantName={tenant.name || 'this tenant'}
        onChange={fetchTenantDetail}
      />

      {/* Summary Stats */}
      <div className="grid gap-4 md:grid-cols-4 lg:grid-cols-8">
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-500" />
              <div>
                <div className="text-xl font-bold">{tenantData.user_summary?.total || 0}</div>
                <p className="text-xs text-muted-foreground">Users</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <UserCheck className="h-5 w-5 text-purple-500" />
              <div>
                <div className="text-xl font-bold">{tenantData.user_summary?.admins || 0}</div>
                <p className="text-xs text-muted-foreground">Admins</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              <div>
                <div className="text-xl font-bold">{tenantData.approvals?.approved || 0}</div>
                <p className="text-xs text-muted-foreground">Approved</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-orange-500" />
              <div>
                <div className="text-xl font-bold">{tenantData.approvals?.pending || 0}</div>
                <p className="text-xs text-muted-foreground">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-red-500" />
              <div>
                <div className="text-xl font-bold">{tenantData.approvals?.rejected || 0}</div>
                <p className="text-xs text-muted-foreground">Rejected</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <FileCheck className="h-5 w-5 text-teal-500" />
              <div>
                <div className="text-xl font-bold">{tenantData.approvals?.approval_rate || 0}%</div>
                <p className="text-xs text-muted-foreground">Approval Rate</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-green-500" />
              <div>
                <div className="text-xl font-bold capitalize">{tenantData.billing?.plan_id || 'Free'}</div>
                <p className="text-xs text-muted-foreground">Plan</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-indigo-500" />
              <div>
                <div className="text-xl font-bold capitalize">{tenant.execution_mode || 'SIMULATED'}</div>
                <p className="text-xs text-muted-foreground">Mode</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Users Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Users ({tenantData.user_summary?.total || 0})
            </CardTitle>
            <CardDescription>Users in this tenant workspace</CardDescription>
          </CardHeader>
          <CardContent>
            {tenantData.users?.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No users in this tenant
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Last Login</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tenantData.users?.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{user.name}</div>
                          <div className="text-xs text-muted-foreground">{user.email}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">{user.role}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={user.status === 'active' ? 'default' : 'secondary'} 
                               className={user.status === 'active' ? 'bg-green-600' : ''}>
                          {user.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right text-sm text-muted-foreground">
                        {user.last_login ? formatDate(user.last_login) : 'Never'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {tenantData.user_summary?.total > 10 && (
              <div className="text-center py-2 text-sm text-muted-foreground">
                +{tenantData.user_summary.total - 10} more users
              </div>
            )}
          </CardContent>
        </Card>

        {/* Activity Trend Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              AI Activity Trend (Last 7 Days)
            </CardTitle>
            <CardDescription>Daily AI execution count</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-48 flex items-end gap-2 px-2">
              {tenantData.activity_trend?.map((day, i) => {
                const maxExec = getMaxExecutions();
                const height = maxExec > 0 ? (day.executions / maxExec) * 100 : 0;
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-2">
                    <div className="w-full flex flex-col items-center justify-end h-36">
                      <span className="text-xs font-medium mb-1">{day.executions}</span>
                      <div 
                        className="w-full bg-primary/80 rounded-t transition-all duration-300 hover:bg-primary"
                        style={{ height: `${Math.max(height, 5)}%` }}
                      />
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' })}
                    </span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Tenant Details Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Tenant Details
            </CardTitle>
            <CardDescription>Configuration and subscription info</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <div className="text-xs text-muted-foreground mb-1">Tenant ID</div>
                <div className="font-mono text-sm break-all select-all">{tenantId}</div>
              </div>
              
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-muted-foreground">Created</span>
                <span>{formatDate(tenant.created_at)}</span>
              </div>
              
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-muted-foreground">Organization</span>
                <span>{tenantData.organization?.name || 'N/A'}</span>
              </div>
              
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-muted-foreground">Execution Mode</span>
                <Badge variant="outline" className="capitalize">{tenant.execution_mode || 'SIMULATED'}</Badge>
              </div>
              
              {/* Subscription Section */}
              <div className="pt-2">
                <div className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  Subscription & Billing
                </div>
                <div className="space-y-3 pl-2">
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-muted-foreground">Plan</span>
                    <Badge 
                      variant="default"
                      className={`capitalize ${
                        tenantData.billing?.plan_id === 'enterprise' ? 'bg-purple-600' :
                        tenantData.billing?.plan_id === 'pro' ? 'bg-blue-600' :
                        tenantData.billing?.plan_id === 'starter' ? 'bg-green-600' : ''
                      }`}
                    >
                      {tenantData.billing?.plan_id || 'Free'}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-muted-foreground">Billing Status</span>
                    <Badge 
                      variant={tenantData.billing?.billing_status === 'active' ? 'default' : 'destructive'}
                      className={tenantData.billing?.billing_status === 'active' ? 'bg-green-600' : ''}
                    >
                      {tenantData.billing?.billing_status || 'Active'}
                    </Badge>
                  </div>
                  {tenantData.billing?.subscription_start && (
                    <div className="flex justify-between items-center py-2 border-b">
                      <span className="text-muted-foreground">Since</span>
                      <span>{formatDate(tenantData.billing.subscription_start)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* User Summary Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              User Summary
            </CardTitle>
            <CardDescription>User distribution and status</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg text-center">
                <div className="text-3xl font-bold text-primary">{tenantData.user_summary?.total || 0}</div>
                <div className="text-sm text-muted-foreground mt-1">Total Users</div>
              </div>
              <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg text-center">
                <div className="text-3xl font-bold text-purple-600">{tenantData.user_summary?.admins || 0}</div>
                <div className="text-sm text-muted-foreground mt-1">Admins</div>
              </div>
              <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg text-center">
                <div className="text-3xl font-bold text-green-600">{tenantData.user_summary?.active || 0}</div>
                <div className="text-sm text-muted-foreground mt-1">Active</div>
              </div>
              <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg text-center">
                <div className="text-3xl font-bold text-slate-400">{tenantData.user_summary?.inactive || 0}</div>
                <div className="text-sm text-muted-foreground mt-1">Inactive</div>
              </div>
            </div>
            
            <div className="mt-6">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-muted-foreground">Active Rate</span>
                <span className="font-medium">
                  {tenantData.user_summary?.total > 0 
                    ? Math.round((tenantData.user_summary?.active / tenantData.user_summary?.total) * 100)
                    : 0}%
                </span>
              </div>
              <Progress 
                value={tenantData.user_summary?.total > 0 
                  ? (tenantData.user_summary?.active / tenantData.user_summary?.total) * 100 
                  : 0} 
                className="h-2"
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Billing History — proves a tenant HAD a paid plan even after downgrade/cancel */}
      <Card data-testid="billing-history-card">
        <CardHeader>
          <div className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-muted-foreground" />
            <CardTitle>Billing History</CardTitle>
          </div>
          <CardDescription>
            Plan changes, activations and renewals over time.
            {billingHistory?.ever_paid_plan && (
              <span className="ml-1">
                Highest paid plan held:{' '}
                <Badge variant="outline" className="capitalize" data-testid="ever-paid-plan">
                  {billingHistory.ever_paid_plan}
                </Badge>
              </span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!billingHistory ? (
            <div className="text-sm text-muted-foreground">Loading history…</div>
          ) : (billingHistory.events || []).length === 0 ? (
            <div className="text-sm text-muted-foreground" data-testid="billing-history-empty">
              No plan/billing events recorded for this tenant yet.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Event</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Detail</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(billingHistory.events || []).map((ev, i) => (
                  <TableRow key={i} data-testid={`billing-history-row-${i}`}>
                    <TableCell className="whitespace-nowrap">{formatDate(ev.timestamp)}</TableCell>
                    <TableCell>{ev.label || ev.type}</TableCell>
                    <TableCell className="capitalize">{ev.plan_id || '—'}</TableCell>
                    <TableCell>{ev.amount != null ? formatCurrency(ev.amount) : '—'}</TableCell>
                    <TableCell className="text-muted-foreground text-xs max-w-[280px] truncate">{ev.detail || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
